import { useCallback, useEffect, useState } from 'react'
import getIsTWAMode from '../helpers/appMode_helper'
import { getTwaNotificationsEnabled } from '../helpers/twaNotifications'

/**
 * Whether OS-level notifications are enabled for this app.
 *
 * In the Android TWA the source of truth is the native app (POST_NOTIFICATIONS), surfaced to the web via
 * the `notificationsEnabled` launch param — see {@link getTwaNotificationsEnabled}. The web's own
 * `Notification.permission` is unreliable there because it tracks Chrome's per-origin web permission, not
 * the native grant. Outside the TWA we fall back to the standard Notification API.
 *
 * The value is re-sampled on focus/visibility so it stays correct after the user returns from the OS
 * settings screen (the native app relaunches the TWA with a fresh param on that round trip).
 *
 * @returns {boolean | null} true = enabled, false = disabled, null = unknown (don't assume either way)
 */
export default function useNotificationsEnabled() {
  const read = useCallback(() => {
    if (getIsTWAMode()) return getTwaNotificationsEnabled()
    if (typeof Notification === 'undefined') return null
    return Notification.permission === 'granted'
  }, [])

  const [enabled, setEnabled] = useState(read)

  useEffect(() => {
    const refresh = () => setEnabled(read())
    refresh()

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('focus', refresh)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('focus', refresh)
    }
  }, [read])

  return enabled
}
